import Typography from "antd/lib/typography";
import clsx from "clsx";
import { ComponentPropsWithoutRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ISkillItem, ISkillLevelItem, ISkillLevelLight } from "@sol/sdk";
import { Cross } from "@sol/uikit/core/icons/Cross";
import { Plus } from "@sol/uikit/core/icons/Plus";
import { Tabs, type TabsProps } from "@sol/uikit/data-display/Tabs/Tabs";
import { Button } from "@sol/uikit/general/Button/Button";

import styles from "./SkillLevelsTabs.module.css";

type Props = {
    skill: ISkillItem;
    value?: ISkillLevelItem[];
    onLevelChange?: (skillLevels: ISkillLevelItem[]) => void;
    isReadOnly?: boolean;
} & ComponentPropsWithoutRef<"div">;

const SkillLevelsTabs = ({ skill, value = [], onLevelChange, isReadOnly, className }: Props) => {
    const [t] = useTranslation();

    const skillLevels = useMemo(() => [skill.skillLevel1, skill.skillLevel2, skill.skillLevel3], [skill]);

    const selectedLevel = value.find(level => level.skill === skill["@id"]);

    const defaultActiveKey = selectedLevel?.["@id"] || skillLevels[0]["@id"];

    const isLevelSelected = (levelId: string): boolean => {
        return value.some(level => level["@id"] === levelId);
    };

    const handleLevelToggle = (levelId: string) => {
        const isSelected = isLevelSelected(levelId);

        if (isSelected) {
            // Remove this level
            onLevelChange?.(value.filter(level => level["@id"] !== levelId));
        } else {
            // Add this level (remove other levels of same skill first)
            const levelToAdd = skillLevels.find(level => level["@id"] === levelId);
            if (levelToAdd) {
                onLevelChange?.([
                    ...value.filter(level => level.skill !== skill["@id"]),
                    { ...levelToAdd, skill: skill["@id"] } as ISkillLevelItem,
                ]);
            }
        }
    };

    const TabContent = ({ skillLevel }: { skillLevel: ISkillLevelLight }) => {
        const isSelected = isLevelSelected(skillLevel["@id"]);

        return (
            <>
                <Typography.Title level={3} className="mb-0! text-ant-h4">
                    {skill.officialTitle}
                </Typography.Title>
                <div className="grid h-full grid-cols-2 grid-rows-none gap-6 overflow-hidden">
                    <div className="col-span-1 overflow-auto rounded-ant-lg bg-ant-primary p-ant">
                        <Typography.Title level={4} ellipsis={{ rows: 1, tooltip: true }} className="text-ant-h2">
                            {t("component.shared.SkillLevelsTabs.actions")}
                        </Typography.Title>
                        <Typography dangerouslySetInnerHTML={{ __html: skillLevel.actions }} />
                    </div>
                    <div className="col-span-1 overflow-auto rounded-ant-lg bg-ant-primary p-ant">
                        <Typography.Title level={4} ellipsis={{ rows: 1, tooltip: true }} className="text-ant-h2">
                            {t("component.shared.SkillLevelsTabs.evalCriteria")}
                        </Typography.Title>
                        <Typography dangerouslySetInnerHTML={{ __html: skillLevel.evalCriteria }} />
                    </div>
                </div>

                {onLevelChange && !isReadOnly && (
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            className="size-8!"
                            aria-label={t(
                                isSelected
                                    ? "page.briefs.create.skills.removeSkillLevel"
                                    : "page.briefs.create.skills.addSkillLevel",
                                {
                                    skillLevelName: skill.shortTitle,
                                    skillLevelNumber: skillLevel.level,
                                },
                            )}
                            onClick={() => handleLevelToggle(skillLevel["@id"])}
                            data-cy="add-skill-level"
                            type="primary"
                            icon={isSelected ? <Cross /> : <Plus />}
                            danger={isSelected}
                        />
                        <Typography.Text>
                            {t(
                                isSelected
                                    ? "page.briefs.create.skills.removeLevel"
                                    : "page.briefs.create.skills.addLevel",
                                {
                                    skillLevelName: skill.shortTitle,
                                    skillLevelNumber: skillLevel.level,
                                },
                            )}
                        </Typography.Text>
                    </div>
                )}
            </>
        );
    };

    const items: TabsProps["items"] = skillLevels.map(skillLevel => ({
        key: skillLevel["@id"],
        label: t("component.shared.SkillLevelsTabs.level", { level: skillLevel.level }),
        children: <TabContent skillLevel={skillLevel} />,
    }));

    return (
        <Tabs
            items={items}
            defaultActiveKey={defaultActiveKey}
            className={clsx(styles.skillLevelsTabs, className, "overflow-hidden")}
            destroyOnHidden
        />
    );
};

export default SkillLevelsTabs;
